import { createColumnHelper } from "@tanstack/react-table";
import { T } from "@tolgee/react";
import { AlertTriangle, ExternalLink, Loader2 } from "lucide-react";
import { InfoTooltip } from "#/components/ui/info-tooltip";
import {
	isTransactionInFlight,
	type TransactionKind,
	type TransactionRow,
	type TransactionStatus,
} from "#/features/portfolio/lib/transaction-history-rows";
import { getArbiscanTxUrl } from "#/lib/explorer";
import { formatDateTime, formatUsd, toNumber } from "#/lib/formatters";

export type { TransactionRow };

const col = createColumnHelper<TransactionRow>();

/** Deposits read green, both withdrawal legs red — same as before the merge. */
const KIND_LABELS: Record<TransactionKind, { key: string; className: string }> =
	{
		deposit: { key: "portfolio.deposit", className: "text-trade-green" },
		// Distinguished on purpose: the two legs of a withdrawal used to both
		// render as "Withdrawal", which read as one cash-out charged twice.
		"withdraw-to-wallet": {
			key: "actions.withdraw-to-wallet",
			className: "text-trade-red",
		},
		"withdraw-external": {
			key: "portfolio.tx-external-withdraw",
			className: "text-trade-red",
		},
	};

const STATUS_KEYS: Record<TransactionStatus, string> = {
	queued: "portfolio.tx-status.queued",
	preparing: "portfolio.tx-status.preparing",
	confirming: "portfolio.tx-status.confirming",
	completed: "portfolio.tx-status.completed",
	failed: "portfolio.tx-status.failed",
};

function StatusCell({ row }: { row: TransactionRow }) {
	const { status, kind } = row;

	if (status === "failed") {
		// `wallet_transfers.error` is written for operators and can name our own
		// infrastructure ("treasury ETH insufficient…"), so it never reaches the
		// UI — see `transfer-status-sync.tsx`. The user gets the reassurance that
		// matters instead: the money never left, try again.
		return (
			<InfoTooltip
				content={
					<T
						keyName={
							kind === "deposit"
								? "toast.deposit-rejected"
								: "toast.withdrawal-rejected"
						}
					/>
				}
				className="inline-flex items-center gap-1 text-label2-medium text-[var(--color-theme-status-negative)]"
			>
				<AlertTriangle className="h-3 w-3 shrink-0" />
				<T keyName={STATUS_KEYS.failed} />
			</InfoTooltip>
		);
	}

	if (isTransactionInFlight(status)) {
		return (
			<span className="inline-flex items-center gap-1 text-label2-medium text-primary">
				<Loader2 className="h-3 w-3 shrink-0 animate-spin" />
				<T keyName={STATUS_KEYS[status]} />
			</span>
		);
	}

	return (
		<span className="text-label2-medium text-muted-foreground">
			<T keyName={STATUS_KEYS.completed} />
		</span>
	);
}

export const transactionHistoryColumns = [
	col.accessor("time", {
		header: () => <T keyName="portfolio.time" />,
		cell: ({ row, getValue }) => {
			const { hash } = row.original;
			const formatted = formatDateTime(getValue());
			// An in-flight row has no tx hash until it's broadcast.
			if (!hash) {
				return (
					<span className="text-label2-tabular-medium text-muted-foreground">
						{formatted}
					</span>
				);
			}
			return (
				<a
					href={getArbiscanTxUrl(hash)}
					target="_blank"
					rel="noreferrer"
					aria-label="View transaction on Arbiscan"
					className="inline-flex items-center gap-1 text-label2-tabular-medium text-muted-foreground transition-colors hover:text-foreground"
				>
					{formatted}
					<ExternalLink className="h-3 w-3" />
				</a>
			);
		},
	}),
	col.accessor("kind", {
		header: () => <T keyName="portfolio.type" />,
		cell: ({ getValue }) => {
			const { key, className } = KIND_LABELS[getValue()];
			return (
				<span className={`text-label2-medium ${className}`}>
					<T keyName={key} />
				</span>
			);
		},
	}),
	col.accessor("status", {
		header: () => <T keyName="portfolio.status" />,
		cell: ({ row }) => <StatusCell row={row.original} />,
	}),
	col.accessor("usdc", {
		header: () => (
			<span className="block text-right">
				<T keyName="portfolio.amount" />
			</span>
		),
		cell: ({ getValue }) => (
			<span className="block text-right text-label2-tabular-medium">
				{formatUsd(toNumber(getValue()))}
			</span>
		),
	}),
	col.accessor("fee", {
		header: () => (
			<span className="block text-right">
				<T keyName="portfolio.fee" />
			</span>
		),
		cell: ({ getValue }) => (
			<span className="block text-right text-label2-tabular-medium text-muted-foreground">
				{getValue() != null ? formatUsd(toNumber(getValue())) : "--"}
			</span>
		),
	}),
];
